/**
 * accountChooserUrl — make Google ASK which account signs in (2026-10-10).
 *
 * Miles connected "another Google Drive account" twice and Google silently used the account already signed in on
 * his browser (Justin's): an authorize URL without `prompt` lets Google pick that session for an app it has seen.
 * Composio's API forwards no prompt or login_hint (connectionVerify.ts), so the only place the ask can ride is the
 * minted URL itself — and only when that URL IS Google's own authorize endpoint. An opaque hop (Composio's own link,
 * which redirects later) is returned exactly as minted: rewriting a link whose next stop we cannot see could break
 * the sign-in it exists to start. The post-connect identity check is the belt either way (who actually signed in is
 * verified server-side, never trusted from the hint — Google's own guidance).
 *
 * PURE. Anything it does not recognise comes back byte for byte.
 */

const GOOGLE_AUTHORIZE_PATHS = new Set(['/o/oauth2/auth', '/o/oauth2/v2/auth']);
const EMAIL_SHAPE = /^[^\s@]{1,64}@[^\s@]{1,190}\.[^\s@]{2,}$/;

/** Is this Google's own OAuth authorize endpoint (never a hop, never another provider)? */
export function isDirectGoogleAuthorizeUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(String(url));
    return parsed.protocol === 'https:'
      && /(^|\.)accounts\.google\.com$/i.test(parsed.hostname)
      && GOOGLE_AUTHORIZE_PATHS.has(parsed.pathname.replace(/\/+$/, ''));
  } catch {
    return false;
  }
}

/**
 * A direct Google authorize URL with `select_account` added to its `prompt` (kept beside any value already there,
 * `consent` included), and `login_hint` set to the account the person said they are connecting when the URL names
 * none. A `prompt=none` flow is never touched: it means "do not show anything", and select_account cannot join it.
 */
export function withGoogleAccountChooser(
  url: string,
  opts: { loginHint?: string | null } = {},
): string {
  if (!isDirectGoogleAuthorizeUrl(url)) return url;
  try {
    const parsed = new URL(url);
    const tokens = String(parsed.searchParams.get('prompt') || '')
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (tokens.includes('none')) return url;
    let changed = false;
    if (!tokens.includes('select_account')) {
      tokens.push('select_account');
      parsed.searchParams.set('prompt', tokens.join(' '));
      changed = true;
    }
    const hint = String(opts.loginHint || '').trim();
    if (hint && EMAIL_SHAPE.test(hint) && !parsed.searchParams.get('login_hint')) {
      parsed.searchParams.set('login_hint', hint);
      changed = true;
    }
    return changed ? parsed.toString() : url;
  } catch {
    return url;
  }
}
